<template>
  <div class="w-full bg-truegray-100">
    <!-- HERO -->
    <div
      class="post-intro z-40 relative px-8 py-16 lg:p-16 pt-0 text-white bg-cover"
      :style="{ backgroundImage: 'url(/img/cloud-bg.jpg)' }"
    >
      <div class="pt-12 lg:pt-24">
        <h1>CareerWise Cloud</h1>
        <h4 class="mb-12">
          Your portal to the latest posts from the CareerWise community.
        </h4>
        <PostsFilter :parent="component_id" />
      </div>
    </div>

    <!-- CONTENT -->
    <div
      id="cloudContent"
      :class="loaded ? 'bg-truegray-100' : 'bg-truegray-500'"
      class="w-full px-8 py-16 lg:p-16"
    >
      <!-- Filter information -->
      <div class="my-8 pb-4 border-b border-truegray-300">
        <div v-if="loaded" class="flex space-between items-center w-full">
          <h2 class="text-4xl">Latest {{ current_filters_text.category }}</h2>
          <button
            class="btn btn-trans-black ml-auto mb-0"
            @click="showCloudStatModal"
          >
            View Cloud Stats
          </button>
        </div>
        <p
          v-if="
            current_filters_text.keywords ||
            current_filters_text.region ||
            current_filters_text.tag ||
            current_filters_text.year
          "
          class="text-cw-alt text-bold flex items-center mb-2"
        >
          Active Filters:
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z"
            />
          </svg>
        </p>
        <p v-if="current_filters_text.keywords">
          Keywords:
          <span class="text-cw-alt">{{ current_filters_text.keywords }}</span>
        </p>
        <p v-if="current_filters_text.region">
          Region:
          <span class="text-cw-alt">{{ current_filters_text.region }}</span>
        </p>
        <p v-if="current_filters_text.tag">
          Tag:
          <span class="text-cw-alt">{{ current_filters_text.tag }}</span>
        </p>
        <p v-if="current_filters_text.year">
          Year:
          <span class="text-cw-alt">{{ current_filters_text.year }}</span>
        </p>
      </div>

      <div
        class="transition-all duration-300 transform"
        :class="[
          loaded ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0',
        ]"
      >
        <!-- Grid toggle -->
        <div class="w-full flex space-x-6 items-start justify-between mb-8">
          <div class="flex flex-col items-start justify-center mb-4">
            <FormulateInput
              key="orgOnly"
              name="orgOnly"
              type="toggle"
              :val="filterCloudBy.org_posts_only"
              :label="
                filterCloudBy.org_posts_only
                  ? 'View Contributor Posts'
                  : 'View All Posts'
              "
              @input="togglePostView"
            />
            <p class="text-xs text-truegray-500 mt-2">
              Toggle on to view Contributor posts only. Toggle off to view all
              posts.
            </p>
          </div>
          <div class="flex space-x-6">
            <div
              title="Card View"
              :class="
                imageCard ? 'text-cw-700 border-cw-700' : 'text-truegray-300'
              "
              class="p-2 border cursor-pointer flex items-center justify-center rounded"
              @click="imageCard = true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </div>
            <div
              title="List View"
              :class="
                !imageCard ? 'text-cw-700 border-cw-700' : 'text-truegray-300'
              "
              class="p-2 border cursor-pointer flex items-center justify-center rounded"
              @click="imageCard = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>

        <div
          v-if="loaded && imageCard"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-x-4 gap-y-6"
        >
          <PostsCard
            v-for="(post, i) in posts"
            :id="post.id"
            :key="i"
            :ref="'posts-' + i"
            :post="post"
            :parent="component_id"
            :tenant-cloud="true"
            :admin_menu="false"
          />
        </div>

        <div
          v-if="loaded && !imageCard"
          class="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <PostCardNoImage
            v-for="(post, i) in posts"
            :id="post.id"
            :key="i"
            :ref="'cloud-post-' + i"
            :post="post"
          />
        </div>
      </div>

      <div
        v-if="!loaded"
        class="h-52 flex flex-col justify-center items-center"
      >
        <lottie :options="loadingAnimationOptions" />
        <span class="text-white text-center">Loading posts</span>
      </div>

      <p v-if="loaded && posts.length < 1" class="text-md">
        We could not find any posts...
      </p>

      <!-- PAGINATION -->
      <div
        v-if="(pagination.previouspage || pagination.nextpage) && loaded"
        class="flex justify-between items-center mt-8 h-10"
      >
        <span
          :disabled="!pagination.previouspage"
          :class="[
            !pagination.previouspage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.previouspage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 17l-5-5m0 0l5-5m-5 5h12"
            />
          </svg>
        </span>
        <span class="text-center text-xs"
          >Page {{ pagination.current_page }} of
          {{ pagination.total_pages }}</span
        >
        <span
          :disabled="!pagination.nextpage"
          :class="[
            !pagination.nextpage
              ? 'opacity-25 cursor-not-allowed'
              : 'hover:text-gray-900 cursor-pointer transition ease-in-out duration-200',
          ]"
          class="text-gray-500"
          @click="getPosts(pagination.nextpage)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { shared } from '../../../../../js/mixins/shared.js'
import PostsFilter from '../../../../cloud/components/PostsFilter.vue'
import PostCardNoImage from './PostCard.vue'
import PostsCard from '../../../../../shared/components/PostsCard.vue'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    PostsFilter,
    PostCardNoImage,
    PostsCard,
  },

  mixins: [shared],

  data() {
    return {
      component_id: false,

      loaded: false,
      loadingAnimationOptions: {
        animationData: loadingAnimation.default,
      },

      loadingAnimate: false,
      posts: [],
      fromPostCount: 0,

      stats_leaders: {},

      post_leaders: [],
      share_leaders: [],
      share_avg_leaders: [],

      current_filters_text: {
        category: null,
        keywords: null,
        region: null,
        tag: null,
        year: null,
      },

      current_submitted_filters: {
        category: 'Posts',
        keywords: null,
        region: null,
        tag: null,
        year: null,
      },

      filter_posts_options: {},
      oldEvents: false,

      imageCard: true,

      window: { width: 0, height: 0 },
      inputClass:
        'flex justify-center items-center sm:text-sm text-black focus:text-black active:text-black focus:outline-none active:outline-none transition-all duration-100 ease-in-out rounded bg-white px-4 h-12',

      pagination: {
        current_page: null,
        total_pages: null,
        per_page: 9,
        next_page: null,
        previous_page: null,
        last_page_url: null,
        next_page_url: null,
        prev_page_url: null,
        path: null,
        nextpage: null,
        previouspage: null,
      },
    }
  },

  computed: {
    ...mapGetters({
      settings: 'shared/getSettings',
      filterCloudBy: 'careerwise/getFilterCloudBy',
    }),
  },

  watch: {
    posts: {
      handler: function () {
        this.setTags()
      },

      deep: true,
    },

    stats_leaders: {
      handler: function (val) {
        let post_leaders_arr = val.posts.slice(0, 3)
        let final_post_leaders_arr = post_leaders_arr.map((obj) => {
          return obj.tenant_id
        })

        let share_leaders_arr = val.shares.slice(0, 3)
        let final_share_leaders_arr = share_leaders_arr.map((obj) => {
          return obj.tenant_id
        })

        let share_avg_leaders_arr = val.share_avg.slice(0, 3)
        let final_share_avg_leaders_arr = share_avg_leaders_arr.map((obj) => {
          return obj.tenant_id
        })

        let store_leaders = {
          posts: final_post_leaders_arr,
          shares: final_share_leaders_arr,
          share_avg: final_share_avg_leaders_arr,
          flagged_tenants: [
            ...final_post_leaders_arr,
            ...final_share_leaders_arr,
            ...final_share_avg_leaders_arr,
          ],
        }

        this.dispatchSetCloudTenantLeaders(store_leaders)
      },

      deep: true,
    },

    filterCloudBy: {
      handler: function (filter) {
        this.$set(this, 'filter_posts_options', filter)
      },

      deep: true,
    },

    current_submitted_filters: {
      handler: function (val) {
        if (this.current_submitted_filters.locked) {
          return
        }

        if (val.category) {
          this.settings.post_categories.forEach((cat) => {
            if (cat.value === val.category) {
              if (cat.text === 'All') {
                this.current_filters_text.category = 'Posts'
              } else {
                this.current_filters_text.category = cat.text
              }
            }
          })
        } else {
          this.current_filters_text.category = 'Posts'
        }

        if (val.keywords) {
          this.current_filters_text.keywords = val.keywords
        } else {
          this.current_filters_text.keywords = null
        }

        if (val.region) {
          this.settings.tag_regions.forEach((region) => {
            if (region.value === val.region) {
              this.current_filters_text.region = region.text
            }
          })
        } else {
          this.current_filters_text.region = null
        }

        if (val.tag) {
          this.settings.tags.forEach((tag) => {
            if (tag.value === val.tag) {
              this.current_filters_text.tag = tag.text
            }
          })
        } else {
          this.current_filters_text.tag = null
        }

        if (val.year) {
          this.settings.tag_years.forEach((year) => {
            if (year.value === val.year) {
              this.current_filters_text.year = year.text
            }
          })
        } else {
          this.current_filters_text.year = null
        }
      },

      deep: true,
    },
  },

  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
  },

  async mounted() {
    await this.dispatchRedirectForUpdates({
      router: this.$router,
      updatesRequired: ['Tags'],
    })
    this.$set(this, 'component_id', this.makeId())

    this.dispatchSetFilterCloudBy({ reset: true })
    this.$set(this, 'filter_posts_options', {})

    // Default our first API call to news
    // this.dispatchSetFilterCloudBy({ category : 1 });

    setTimeout(() => {
      this.getPosts()
    }, 10)

    this.getCloudCategoryLeaders()

    EventBus.$on('getCloudPostsWithFilterOptions', (data) => {
      if (data.parent === this.component_id || data === this.component_id) {
        this.$set(this, 'oldEvents', data.oldEvents)
        this.getPosts()
      }
    })

    EventBus.$on('cloneCloudPost', (id) => {
      this.saveCloudPost(id)
    })
  },

  unmounted() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    ...mapActions({
      dispatchSetCloudTenantLeaders: 'careerwise/dispatchSetCloudTenantLeaders',
      dispatchSetFilterCloudBy: 'careerwise/dispatchSetFilterCloudBy',
      dispatchRedirectForUpdates: 'careerwise/dispatchRedirectForUpdates',
    }),

    handleResize() {
      this.window.width = window.innerWidth
      this.window.height = window.innerHeight
    },

    resetFilters() {
      EventBus.$emit('resetFilters')
    },

    showCloudStatModal() {
      EventBus.$emit('showModal', 'cloud-stats')
    },

    togglePostView(value) {
      this.$set(this.filterCloudBy, 'org_posts_only', value)
      this.getPosts() // Refresh posts based on the new filter
    },

    setTags() {
      if (this.posts.length > 0) {
        this.posts.forEach((post) => {
          //general tags
          let general_arr = []
          this.settings.tags.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              general_arr.push(i.text)
            }
          })
          post.general_tags = general_arr

          // industry tags
          let industry_arr = []
          this.settings.tag_industries.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              industry_arr.push(i.text)
            }
          })
          post.industry_tags = industry_arr

          // Year tags
          let year_arr = []
          this.settings.tag_years.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              year_arr.push(i.text)
            }
          })
          post.year_tag = year_arr

          // Region tags
          let region_arr = []
          this.settings.tag_regions.forEach((i) => {
            if (post.tag_titles.includes(i.text)) {
              region_arr.push(i.text)
            }
          })
          post.region_tag = region_arr
        })
      }
    },

    /**
      ============================ GET POSTS WITH FILTER OPTION ===============
    */
    getPosts(gotopage = null) {
      //disable post filter during API request
      EventBus.$emit('cloudpost_api_request_sent')
      this.$set(this, 'loaded', false)
      let submitData = {}

      submitData.search = this.filter_posts_options

      // Pagination page?
      if (gotopage) {
        submitData.page = gotopage
      }

      if (this.oldEvents) {
        submitData.oldEvents = true
      }

      // Get this post...
      axios
        .post('/api/auth/manager-cloud-posts', submitData)
        .then((response) => {
          let result = response.data
          if (result.success) {
            this.$set(this, 'posts', result.posts)
            this.$set(this, 'loaded', true)

            // pagination.total = total # of posts. / by per_page, + 1. Pagination doesnt show when not needed so +1 is never wrong on single page
            let total_pages =
              Math.floor(
                response.data.pagination.total /
                  response.data.pagination.per_page,
              ) + 1
            this.$set(
              this.pagination,
              'current_page',
              response.data.pagination.current_page,
            )
            this.$set(this.pagination, 'total_pages', total_pages)
            this.$set(
              this.pagination,
              'per_page',
              response.data.pagination.per_page,
            )
            this.$set(
              this.pagination,
              'last_page_url',
              response.data.pagination.last_page_url,
            )
            this.$set(
              this.pagination,
              'next_page_url',
              response.data.pagination.next_page_url,
            )
            this.$set(
              this.pagination,
              'prev_page_url',
              response.data.pagination.prev_page_url,
            )
            this.$set(
              this.pagination,
              'path',
              response.data.pagination.next_page_path,
            )

            let prev = null
            if (this.pagination.prev_page_url) {
              prev = this.pagination.prev_page_url.split('=')
              prev = prev[1]
            }

            let next = null
            if (this.pagination.next_page_url) {
              next = this.pagination.next_page_url.split('=')
              next = next[1]
            }

            this.$set(this.pagination, 'previouspage', prev)
            this.$set(this.pagination, 'nextpage', next)

            // Update the saved list...
            if (response.data.saved) {
              this.$store.commit(
                'careerwise/UPDATE_SAVED_CLOUDPOSTS',
                response.data.saved,
              )
            }

            // Update our active filter - Commented out version does not work. TBD investigate. Setting data to somethiing that has been mapped to the store appears to inherit that map.
            // In this case it means that the current_submitted_filters data point is being updated every time the filterCloudBy is being updated in the store from the postCloudFilter component.
            Object.keys(this.filter_posts_options).forEach((key) => {
              this.current_submitted_filters[key] =
                this.filter_posts_options[key]
            })
            // let filter = this.filter_posts_options;
            // this.$set(this, 'current_submitted_filters', filter);
          } else {
            //...
          }

          // const contentElement = document.getElementById('content')
          // const topOffset =
          //   contentElement.getBoundingClientRect().top + window.scrollY
          // window.scrollTo({ top: topOffset, behavior: 'smooth' })
          const contentElement = document.getElementById('cloudContent')
          contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
          EventBus.$emit('filter_posts_search_response_completed')
        })
    },

    /**
     * Save cloud post to posts
     */
    unsaveCloudPost(id) {
      let submitData = {}
      submitData.post_id = id

      // Get this post...
      axios.post('/api/auth/manager-cloud-post-unsave', submitData)
    },

    getCloudCategoryLeaders() {
      axios.get('/api/cloud-badge-leaders').then((response) => {
        let result = response.data
        if (result.success) {
          this.$set(this, 'stats_leaders', result.leaders)
          // this.$store.commit('set_cloud_category_leaders',result.leaders);
        }
      })
    },
  },
}
</script>

<style scoped>
.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}

.post-intro::before {
  content: '';
  background: #000;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  right: 0;
  opacity: 0.2;
  z-index: -1;
}

.content-wrapper {
  min-height: 20rem;
}

@media screen and (max-width: 1024px) {
  .post-intro::after {
    display: none;
  }
}
</style>
